import { useEffect, useMemo, useState } from 'react';
import { Table, Typography, Button, Modal, Form, Input, message, Tag, Select, Space } from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  provisionAuthUser,
  formatDate,
  formatDateTime,
  fetchOrgDocuments,
  getSignedUrl,
  usePortalPermissions,
  useAuthUser,
  clientResetPasswordRedirectUrl,
  SetupCredentialsNotice,
  sendPasswordSetupLink,
  logAssistResetAudit,
  ORGANIZATION_CUSTOMER_TYPES,
} from '@ubus/shared';
import type { FleetOperator, Organization, OrganizationCustomerType, TripDocument, SendPasswordSetupResult } from '@ubus/shared';
import EntityDrawer from '../components/EntityDrawer';
import AdminListPage from '../components/AdminListPage';
import { TableRowActions } from '../components/TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { localeConfirm, localeSuccess } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';

const { Title, Text } = Typography;

type OrgDocument = TripDocument & {
  trips?: { date: string; pickup_address: string } | null;
};

function Organizations() {
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const { hasPermission } = usePortalPermissions();
  const canCreate = hasPermission('organizations.create');
  const canEdit = hasPermission('organizations.edit');
  const canProvision = hasPermission('organizations.provision');
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [fleetOperators, setFleetOperators] = useState<Pick<FleetOperator, 'id' | 'name' | 'status'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [viewOrg, setViewOrg] = useState<Organization | null>(null);
  const [viewTripCount, setViewTripCount] = useState<number | null>(null);
  const [viewDocuments, setViewDocuments] = useState<OrgDocument[]>([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [form] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState('');
  const [fleetFilter, setFleetFilter] = useState<string | undefined>();
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const operatorNameById = useMemo(
    () => Object.fromEntries(fleetOperators.map((o) => [o.id, o.name])),
    [fleetOperators]
  );

  const filteredOrgs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return orgs.filter((org) => {
      if (fleetFilter === '__none__') {
        if (org.preferred_fleet_operator_id) return false;
      } else if (fleetFilter && org.preferred_fleet_operator_id !== fleetFilter) {
        return false;
      }
      if (!q) return true;
      const fleetName = org.preferred_fleet_operator_id
        ? (operatorNameById[org.preferred_fleet_operator_id] ?? '')
        : '';
      const haystack = [
        org.name,
        org.contact_person,
        org.contact_phone,
        org.contact_email,
        org.address,
        ...(org.notification_emails ?? []),
        fleetName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [orgs, searchQuery, fleetFilter, operatorNameById]);

  const hasListFilters = !!searchQuery.trim() || !!fleetFilter;

  useEffect(() => {
    void fetchOrgs();
    void fetchFleetOperators();
  }, []);

  const fetchFleetOperators = async () => {
    const { data, error } = await supabase
      .from('fleet_operators')
      .select('id, name, status')
      .order('name');
    if (error) {
      message.error(error.message);
      return;
    }
    setFleetOperators(data ?? []);
  };

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        message.error(error.message);
      } else {
        setOrgs(data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingOrg(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Organization) => {
    setEditingOrg(record);
    form.setFieldsValue({
      ...record,
      notification_emails: (record.notification_emails || []).join('; '),
    });
    setModalVisible(true);
  };

  const handleView = async (record: Organization) => {
    setViewOrg(record);
    setViewTripCount(null);
    setViewDocuments([]);
    setViewLoading(true);

    try {
      const [countResult, documents] = await Promise.all([
        supabase
          .from('trips')
          .select('*', { count: 'exact', head: true })
          .eq('org_id', record.id),
        fetchOrgDocuments(record.id),
      ]);

      if (countResult.error) {
        message.error(countResult.error.message);
      } else {
        setViewTripCount(countResult.count ?? 0);
      }
      setViewDocuments(documents as OrgDocument[]);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('organizations.detailsLoadFailed'));
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownloadDocument = async (filePath: string) => {
    const url = await getSignedUrl('documents', filePath);
    if (url) {
      window.open(url, '_blank');
    } else {
      message.error(t('organizations.downloadUrlFailed'));
    }
  };

  const handleDelete = (id: string) => {
    localeConfirm(t, {
      title: t('organizations.deleteTitle'),
      content: t('organizations.deleteContent'),
      okText: t('organizations.delete'),
      cancelText: t('common.cancel'),
      okType: 'danger',
      onOk: async () => {
        const { error } = await supabase
          .from('organizations')
          .delete()
          .eq('id', id);

        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
        } else {
          message.success(t('organizations.deleted'));
          fetchOrgs();
        }
      }
    });
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      const preferredFleet =
        values.preferred_fleet_operator_id && String(values.preferred_fleet_operator_id).trim()
          ? String(values.preferred_fleet_operator_id)
          : null;
      const customerType =
        values.customer_type && String(values.customer_type).trim()
          ? (String(values.customer_type) as OrganizationCustomerType)
          : null;
      const orgData = {
        ...values,
        preferred_fleet_operator_id: preferredFleet,
        customer_type: customerType,
        notification_emails: values.notification_emails
          ? String(values.notification_emails).split(/[;,]/).map((e: string) => e.trim()).filter(Boolean)
          : [],
      };
      delete (orgData as Record<string, unknown>).initial_password;

      if (editingOrg) {
        const editedId = editingOrg.id;
        const { error } = await supabase
          .from('organizations')
          .update(orgData)
          .eq('id', editedId);

        if (error) {
          message.error(error.message);
        } else {
          message.success(t('organizations.updated'));
          setModalVisible(false);
          await afterMutation(editedId, fetchOrgs);
        }
      } else {
        const { data: created, error } = await supabase
          .from('organizations')
          .insert([orgData])
          .select('id')
          .single();

        if (error) {
          message.error(error.message);
          return;
        }

        try {
          if (canProvision) {
            const auth = await provisionAuthUser({
              role: 'client',
              email: String(values.contact_email),
              redirectTo: clientResetPasswordRedirectUrl(),
            });

            localeSuccess(t, {
              title: t('organizations.loginCreated'),
              width: 560,
              content: (
                <SetupCredentialsNotice
                  email={auth.email}
                  setupLink={auth.setupLink}
                  extraNote={t('organizations.loginShare')}
                />
              ),
              okText: t('organizations.ok'),
            });
          } else {
            message.success(t('organizations.created', { defaultValue: 'Organization created' }));
          }
          setModalVisible(false);
          await afterMutation(created?.id, fetchOrgs);
        } catch (authErr: unknown) {
          const msg = authErr instanceof Error ? authErr.message : t('organizations.loginCreateFailed');
          message.warning(t('organizations.loginWarning', { message: msg }));
          setModalVisible(false);
          await afterMutation(created?.id, fetchOrgs);
        }
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('organizations.saveFailed'));
    }
  };

  const showSetupLinkSuccess = (
    title: string,
    result: Pick<SendPasswordSetupResult, 'email' | 'setupLink' | 'emailed' | 'recipient'>
  ) => {
    localeSuccess(t, {
      title,
      width: 560,
      content: (
        <SetupCredentialsNotice
          email={result.email}
          setupLink={result.setupLink}
          extraNote={
            result.emailed
              ? t('organizations.setupLinkEmailed', { email: result.recipient || result.email })
              : t('organizations.loginShare')
          }
        />
      ),
      okText: t('organizations.ok'),
    });
  };

  const handleSendSetupLink = (record: Organization) => {
    if (!canProvision) {
      message.warning(t('organizations.setupLinkNoPermission'));
      return;
    }
    const email = (record.contact_email || '').trim().toLowerCase();
    if (!email) {
      message.warning(t('organizations.setupLinkNoEmail'));
      return;
    }

    localeConfirm(t, {
      title: t('organizations.sendSetupLink'),
      content: t('organizations.setupLinkConfirm', { name: record.name, email }),
      okText: t('organizations.sendSetupLink'),
      onOk: async () => {
        const redirectTo = clientResetPasswordRedirectUrl();
        try {
          const result = await sendPasswordSetupLink({
            email,
            redirectTo,
            sendEmail: true,
          });
          logAssistResetAudit({
            actorId: user?.id,
            targetType: 'organization',
            targetId: record.id,
            channel: result.emailed ? 'email_link' : 'link_only',
            emailed: result.emailed,
          });
          showSetupLinkSuccess(t('organizations.setupLinkSent'), result);
        } catch (firstErr) {
          // Auth user may not exist yet — provision then send/show link
          try {
            const auth = await provisionAuthUser({
              role: 'client',
              email,
              redirectTo,
            });
            let result: SendPasswordSetupResult = {
              email: auth.email,
              setupLink: auth.setupLink ?? null,
              emailed: false,
              recipient: email.endsWith('@ubus.local') ? null : email,
            };
            try {
              result = await sendPasswordSetupLink({
                email,
                redirectTo,
                sendEmail: true,
              });
            } catch {
              // Keep provisioned link if re-send fails
            }
            logAssistResetAudit({
              actorId: user?.id,
              targetType: 'organization',
              targetId: record.id,
              channel: result.emailed ? 'email_link' : 'link_only',
              emailed: result.emailed,
            });
            showSetupLinkSuccess(t('organizations.setupLinkSent'), result);
          } catch (provisionErr) {
            const msg =
              provisionErr instanceof Error
                ? provisionErr.message
                : firstErr instanceof Error
                  ? firstErr.message
                  : t('organizations.setupLinkFailed');
            message.error(msg);
          }
        }
      },
    });
  };

  const columns: TableProps<Organization>['columns'] = [
    {
      title: t('organizations.columns.name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      defaultSortOrder: 'ascend',
    },
    {
      title: t('organizations.columns.customerType'),
      dataIndex: 'customer_type',
      key: 'customer_type',
      width: 120,
      render: (v: OrganizationCustomerType | null | undefined) =>
        v ? t(`organizations.customerTypes.${v}`) : '—',
    },
    {
      title: t('organizations.columns.contactPerson'),
      dataIndex: 'contact_person',
      key: 'contact_person',
      width: 150,
    },
    {
      title: t('organizations.columns.phone'),
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      width: 140,
    },
    {
      title: t('organizations.columns.email'),
      dataIndex: 'contact_email',
      key: 'contact_email',
      width: 220,
      ellipsis: true,
    },
    {
      title: t('organizations.columns.preferredFleet'),
      dataIndex: 'preferred_fleet_operator_id',
      key: 'preferred_fleet_operator_id',
      width: 160,
      ellipsis: true,
      render: (id: string | null | undefined) =>
        id ? operatorNameById[id] ?? id : '—',
    },
    {
      title: t('organizations.columns.address'),
      dataIndex: 'address',
      key: 'address',
      ellipsis: { showTitle: true },
    },
    {
      title: t('organizations.columns.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (val: string) => formatDateTime(val),
      sorter: (a, b) => a.created_at.localeCompare(b.created_at),
    },
    {
      title: t('organizations.columns.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      render: (val: string) => formatDateTime(val),
      sorter: (a, b) => a.updated_at.localeCompare(b.updated_at),
    },
    {
      title: t('organizations.columns.actions'),
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <TableRowActions>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleView(record)}>
            {t('organizations.view')}
          </Button>
          {canEdit ? (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              {t('organizations.edit')}
            </Button>
          ) : null}
          {canProvision ? (
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleSendSetupLink(record)}
            >
              {t('organizations.sendSetupLink')}
            </Button>
          ) : null}
          {canEdit ? (
            <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
              {t('organizations.delete')}
            </Button>
          ) : null}
        </TableRowActions>
      ),
    },
  ];

  return (
    <>
      <AdminListPage
        title={t('organizations.title')}
        subtitle={t('organizations.subtitle')}
        extra={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('organizations.add')}
            </Button>
          ) : undefined
        }
        filters={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t('organizations.placeholders.search')}
              style={{ width: 280 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={setSearchQuery}
            />
            <Select
              allowClear
              placeholder={t('organizations.placeholders.allFleets')}
              style={{ minWidth: 180 }}
              value={fleetFilter}
              onChange={setFleetFilter}
              options={[
                { value: '__none__', label: t('organizations.placeholders.noFleet') },
                ...fleetOperators.map((op) => ({ value: op.id, label: op.name })),
              ]}
            />
          </Space>
        }
        resultCount={filteredOrgs.length}
        hasActiveFilters={hasListFilters}
        onClearFilters={() => {
          setSearchQuery('');
          setFleetFilter(undefined);
        }}
      >
        <Table
          dataSource={filteredOrgs}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={onTableChange}
          rowClassName={rowClassName}
          scroll={{ x: 1260 }}
          locale={{ emptyText: hasListFilters ? t('organizations.emptyFiltered') : t('organizations.empty') }}
        />
      </AdminListPage>

      <Modal
        title={editingOrg ? t('organizations.edit') : t('organizations.add')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText={t('organizations.save')}
        cancelText={t('common.cancel')}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label={t('organizations.form.name')}
            rules={[{ required: true, message: t('organizations.form.nameRequired') }]}
          >
            <Input placeholder={t('organizations.form.namePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="customer_type"
            label={t('organizations.form.customerType')}
            extra={t('organizations.form.customerTypeHelp')}
          >
            <Select
              allowClear
              placeholder={t('organizations.form.customerTypePlaceholder')}
              options={ORGANIZATION_CUSTOMER_TYPES.map((value) => ({
                value,
                label: t(`organizations.customerTypes.${value}`),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="contact_person"
            label={t('organizations.form.contactPerson')}
            rules={[{ required: true, message: t('organizations.form.contactPersonRequired') }]}
          >
            <Input placeholder={t('organizations.form.contactPersonPlaceholder')} />
          </Form.Item>
          <Form.Item
            name="contact_phone"
            label={t('organizations.form.contactPhone')}
            rules={[{ required: true, message: t('organizations.form.contactPhoneRequired') }]}
          >
            <Input placeholder={t('organizations.form.contactPhonePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="contact_email"
            label={t('organizations.form.contactEmail')}
            rules={[{ required: true, message: t('organizations.form.contactEmailRequired') }]}
          >
            <Input placeholder={t('organizations.form.contactEmailPlaceholder')} />
          </Form.Item>
          {!editingOrg && canProvision ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
              {t('organizations.form.initialPasswordHelp')}
            </Text>
          ) : null}
          <Form.Item
            name="notification_emails"
            label={t('organizations.form.notificationEmails')}
            extra={t('organizations.form.notificationEmailsHelp')}
          >
            <Input placeholder={t('organizations.form.notificationEmailsPlaceholder')} />
          </Form.Item>
          <Form.Item
            name="preferred_fleet_operator_id"
            label={t('organizations.form.preferredFleet')}
            extra={t('organizations.form.preferredFleetHelp')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('organizations.form.preferredFleetPlaceholder')}
              options={fleetOperators
                .filter((o) => o.status === 'active' || o.id === editingOrg?.preferred_fleet_operator_id)
                .map((o) => ({
                  value: o.id,
                  label: o.status === 'active' ? o.name : `${o.name} (${o.status})`,
                }))}
            />
          </Form.Item>
          <Form.Item
            name="address"
            label={t('organizations.form.address')}
          >
            <Input.TextArea rows={3} placeholder={t('organizations.form.addressPlaceholder')} />
          </Form.Item>
          <Form.Item
            name="requirements"
            label={t('organizations.form.requirements')}
            extra={t('organizations.form.requirementsHelp')}
          >
            <Input.TextArea rows={2} placeholder={t('organizations.form.requirementsPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <EntityDrawer
        open={!!viewOrg}
        onClose={() => setViewOrg(null)}
        title={viewOrg?.name || t('organizations.details.title')}
        loading={viewLoading}
        width={720}
      >
        {viewOrg && !viewLoading && (
          <>
            <Table
              size="small"
              pagination={false}
              showHeader={false}
              dataSource={[
                { key: 'name', label: t('organizations.columns.name'), value: viewOrg.name },
                {
                  key: 'customerType',
                  label: t('organizations.columns.customerType'),
                  value: viewOrg.customer_type
                    ? t(`organizations.customerTypes.${viewOrg.customer_type}`)
                    : '—',
                },
                { key: 'contact', label: t('organizations.columns.contactPerson'), value: viewOrg.contact_person },
                { key: 'phone', label: t('organizations.columns.phone'), value: viewOrg.contact_phone },
                { key: 'email', label: t('organizations.columns.email'), value: viewOrg.contact_email },
                {
                  key: 'preferredFleet',
                  label: t('organizations.details.preferredFleet'),
                  value: viewOrg.preferred_fleet_operator_id
                    ? operatorNameById[viewOrg.preferred_fleet_operator_id] ?? viewOrg.preferred_fleet_operator_id
                    : '—',
                },
                { key: 'address', label: t('organizations.columns.address'), value: viewOrg.address || '—' },
                {
                  key: 'notifications',
                  label: t('organizations.details.notifications'),
                  value: viewOrg.notification_emails?.length > 0
                    ? viewOrg.notification_emails.join(', ')
                    : '—',
                },
                {
                  key: 'requirements',
                  label: t('organizations.details.requirements'),
                  value: viewOrg.requirements?.trim() ? viewOrg.requirements : '—',
                },
                { key: 'created', label: t('organizations.columns.created'), value: formatDateTime(viewOrg.created_at) },
                {
                  key: 'updated',
                  label: t('organizations.columns.updated'),
                  value: formatDateTime(viewOrg.updated_at),
                },
                {
                  key: 'trips',
                  label: t('organizations.details.totalTrips'),
                  value: viewTripCount !== null ? String(viewTripCount) : '—',
                },
              ]}
              columns={[
                { dataIndex: 'label', key: 'label', width: 160, render: (v: string) => <Text strong>{v}</Text> },
                { dataIndex: 'value', key: 'value' },
              ]}
            />

            <Title level={5} style={{ marginTop: 24 }}>{t('organizations.details.documents')}</Title>
            <Table
              dataSource={viewDocuments}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: t('organizations.details.noDocuments') }}
              columns={[
                {
                  title: t('organizations.columns.type'),
                  dataIndex: 'doc_type',
                  key: 'doc_type',
                  render: (t: string) => <Tag>{t.toUpperCase()}</Tag>,
                },
                {
                  title: t('organizations.columns.tripDate'),
                  key: 'trip_date',
                  render: (_, record) =>
                    record.trips?.date ? formatDate(record.trips.date) : '—',
                },
                {
                  title: t('organizations.columns.created'),
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (val: string) => formatDate(val),
                },
                {
                  title: t('organizations.columns.actions'),
                  key: 'actions',
                  render: (_, record) => (
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => void handleDownloadDocument(record.file_path)}
                    >
                      {t('organizations.details.download')}
                    </Button>
                  ),
                },
              ]}
            />
          </>
        )}
      </EntityDrawer>
    </>
  );
}

export default Organizations;
